import React, { useState } from 'react';
import { message } from 'antd';
import { CustomButton, CustomInput } from '@/components';
import { DeleteIcon } from '@/assets/icons/icons';
import {
  formatQuantity,
  MAX_PRODUCT_QUANTITY,
  MAX_QUANTITY_MESSAGE,
  MIN_QUANTITY_MESSAGE,
} from '@/utils/formatQuantity';
import type { SelectedProduct } from './ProductMultiSelectField';

interface ProductQuantityRowsProps {
  selectedProducts: SelectedProduct[];
  setSelectedProducts: (products: SelectedProduct[]) => void;
  getProductName: (id: string) => string;
  updateQuantity: (id: string, delta: number) => void;
  removeProduct: (id: string | number) => void;
  showSerials?: boolean;
  getSerialWarning?: (productId: string) => string | undefined;
  getSerialAriaLabel?: (productId: string) => string;
}

interface ProductSerialInputProps {
  value?: string;
  /** Omitted only under a Form.Item, which injects value and onChange. */
  onChange?: (serial: string) => void;
  className?: string;
  ariaLabel?: string;
}

export const ProductSerialInput: React.FC<ProductSerialInputProps> = ({
  value,
  onChange,
  className = '',
  ariaLabel,
}) => (
  <CustomInput
    placeholder='Serial number (optional)'
    aria-label={ariaLabel}
    value={value ?? ''}
    onChange={(e) => onChange?.(e.target.value)}
    height='36px'
    width='100%'
    border='none'
    bgColor='var(--light-gray)'
    textColor='var(--text-primary)'
    borderRadius='8px'
    placeholderColor='var(--text-secondary)'
    lineHeight='36px'
    className={`${className} font-size-12`.trim()}
  />
);

/** Quantity box that keeps a draft while typing and clamps to the allowed range on blur. */
export const QuantityInput: React.FC<{
  value: number;
  onCommit: (quantity: number) => void;
  width?: string;
  ariaLabel?: string;
}> = ({ value, onCommit, width = '80px', ariaLabel }) => {
  const [draft, setDraft] = useState<string | null>(null);

  const commit = (): void => {
    if (draft === null) return;
    const parsed = parseInt(draft, 10);
    setDraft(null);
    if (isNaN(parsed)) return;
    if (parsed < 1) {
      message.warning(MIN_QUANTITY_MESSAGE);
      onCommit(1);
    } else if (parsed > MAX_PRODUCT_QUANTITY) {
      message.warning(MAX_QUANTITY_MESSAGE);
      onCommit(MAX_PRODUCT_QUANTITY);
    }
  };

  const handleChange = (raw: string): void => {
    const digits = raw.replace(/\D/g, '').slice(0, 12);
    setDraft(digits);
    const parsed = parseInt(digits, 10);
    if (parsed >= 1 && parsed <= MAX_PRODUCT_QUANTITY) onCommit(parsed);
  };

  return (
    <CustomInput
      value={draft ?? formatQuantity(value)}
      inputMode='numeric'
      onFocus={() => setDraft(String(value))}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={commit}
      onPressEnter={commit}
      aria-label={ariaLabel}
      height='36px'
      width={width}
      border='0px'
      bgColor='var(--light-gray)'
      textColor='var(--text-primary)'
      borderRadius='8px'
      lineHeight='38px'
      className='quantity-input-padding font-size-12'
    />
  );
};

//  Selected product quantity shown under the Products, CreateShipmentDrawer and CreateShellShipmentDrawer
const ProductQuantityRows: React.FC<ProductQuantityRowsProps> = ({
  selectedProducts,
  setSelectedProducts,
  getProductName,
  updateQuantity,
  removeProduct,
  showSerials = false,
  getSerialWarning,
  getSerialAriaLabel,
}) => {
  const setQuantityById = (productId: string, quantity: number): void => {
    setSelectedProducts(
      selectedProducts.map((p) => (p.id === productId ? { ...p, quantity } : p))
    );
  };

  const setSerialById = (productId: string, serial_number: string): void => {
    setSelectedProducts(
      selectedProducts.map((p) =>
        p.id === productId ? { ...p, serial_number } : p
      )
    );
  };

  return (
    <>
      {selectedProducts
        .filter((product) => product.id !== 'all_products')
        .map((product) => {
          const serialWarning = showSerials
            ? getSerialWarning?.(product.id)
            : undefined;
          return (
            <div
              key={product.id}
              className={
                showSerials
                  ? 'shipment-product-row shipment-product-row--with-serial'
                  : 'shipment-product-row'
              }
            >
              <div className='shipment-product-row__body'>
                <div className='shipment-product-row__name'>
                  {getProductName(product.id)}
                </div>
                {showSerials && (
                  <ProductSerialInput
                    value={product.serial_number}
                    onChange={(serial) => setSerialById(product.id, serial)}
                    className='shipment-product-row__serial'
                    ariaLabel={getSerialAriaLabel?.(product.id)}
                  />
                )}
                {serialWarning && (
                  <div role='alert' className='shipment-product-row__warning'>
                    {serialWarning}
                  </div>
                )}
              </div>
              <div className='shipment-product-row__controls'>
                <CustomButton
                  text={'-'}
                  iconPosition={'left'}
                  border='none'
                  width='28px'
                  height='28px'
                  borderRadius='80px'
                  bgColor='var(--light-gray)'
                  color='var(--text-primary)'
                  boxShadow='none'
                  onClick={() => updateQuantity(product.id, -1)}
                  padding={'0'}
                />
                <QuantityInput
                  value={product.quantity}
                  onCommit={(quantity) => setQuantityById(product.id, quantity)}
                />
                <CustomButton
                  text={'+'}
                  iconPosition={'left'}
                  border='none'
                  width='28px'
                  height='28px'
                  borderRadius='80px'
                  bgColor='var(--light-gray)'
                  color='var(--text-primary)'
                  boxShadow='none'
                  onClick={() => updateQuantity(product.id, 1)}
                  padding={'0'}
                />
                <span
                  onClick={() => removeProduct(product.id)}
                  className='shipment-product-row__delete'
                >
                  <DeleteIcon />
                </span>
              </div>
            </div>
          );
        })}
    </>
  );
};

export default ProductQuantityRows;
